import React, { useEffect, useState, useCallback } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { Link } from 'react-router-dom';
import { peAccessForUser } from '../pe/peActions';

const API_BASE = process.env.REACT_APP_API_URL;

const COLUMNS = ['Open', 'In Progress', 'Resolved'];
const PRIORITY_COLORS = { Low:'#64748b', Normal:'#60a5fa', High:'#f59e0b', Critical:'#EC2124' };
const ENTITY_LABELS = { process_flow:'Process Flow', jig:'Jig', equipment:'Equipment', test_library:'Test Library' };
const SUPPLIERS = ['gpv','ubiqconn','acmepoint','hk_ringer'];
const SUPPLIER_LABELS = { gpv:'GPV', ubiqconn:'Ubiqconn', acmepoint:'Acmepoint', hk_ringer:'HK Ringer' };

function normalizeRole(r) {
  if (!r) return ''; if (Array.isArray(r)) return (r[0]||'').toLowerCase().trim();
  return String(r).toLowerCase().trim();
}
function isServiceRole(role) {
  return ['admin','quality-manager','production-engineer','service'].includes(role);
}
function isSupplierRole(role) {
  return ['gpv','test-gpv','ubiqconn','acmepoint','hk_ringer'].includes(role);
}

export default function NotesKanban() {
  const { getToken } = useAuth();
  const { user } = useUser();
  const role = normalizeRole(user?.publicMetadata?.role) || normalizeRole(user?.publicMetadata?.roles);
  const canManage = isServiceRole(role);
  const isSupplier = isSupplierRole(role);

  const [columns, setColumns] = useState({ Open:[], 'In Progress':[], Resolved:[] });
  const [loading, setLoading] = useState(true);
  const [showNewNote, setShowNewNote] = useState(false);
  const [entityType, setEntityType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [docNumFilter, setDocNumFilter] = useState('');
  const [blockingOnly, setBlockingOnly] = useState(false);
  const [supplierFilter, setSupplierFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (entityType)    params.set('entityType', entityType);
      if (filterStatus)  params.set('status', filterStatus);
      if (docNumFilter)  params.set('docNum', docNumFilter);
      if (blockingOnly)  params.set('isBlocking', 'true');
      if (supplierFilter) params.set('supplier', supplierFilter);
      const res = await window.fetch(`${API_BASE}/quality/notes/kanban?${params}`, {
        headers: { Authorization:`Bearer ${token}` },
      });
      const data = await res.json();
      if (data.ok) setColumns(data.columns);
    } catch {} finally { setLoading(false); }
  }, [getToken, entityType, filterStatus, docNumFilter, blockingOnly, supplierFilter]);

  useEffect(() => { load(); }, [load]);

  const moveNote = useCallback(async (noteId, newStatus) => {
    try {
      const token = await getToken();
      await window.fetch(`${API_BASE}/quality/notes/${noteId}`, {
        method: 'PATCH',
        headers: { Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      load();
    } catch {}
  }, [getToken, load]);

  const deleteNote = useCallback(async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      const token = await getToken();
      await window.fetch(`${API_BASE}/quality/notes/${noteId}`, {
        method: 'DELETE',
        headers: { Authorization:`Bearer ${token}` },
      });
      load();
    } catch {}
  }, [getToken, load]);

  const toggleBlocking = useCallback(async (noteId, val) => {
    try {
      const token = await getToken();
      await window.fetch(`${API_BASE}/quality/notes/${noteId}`, {
        method: 'PATCH',
        headers: { Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
        body: JSON.stringify({ isBlocking: val }),
      });
      load();
    } catch {}
  }, [getToken, load]);

  const fs = { background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)',
    color:'var(--ni-text)', borderRadius:6, padding:'6px 10px', fontSize:13 };

  return (
    <div>
      {peAccessForUser(user).allowed && <section className="ni-panel" style={{ marginBottom: 20 }}><h2 style={{ marginTop: 0 }}>Resource notes and PE meetings</h2><p>This board keeps notes attached to a process, jig, equipment item or test. Use the PE workspaces for meeting actions, owners, progress and evidence.</p><div className="ni-toolbar" style={{ gap: 12, flexWrap: 'wrap' }}><Link className="ni-button" to="/quality/pe-task-follow-up">PE Task Follow-Up</Link><Link className="ni-button" to="/quality/pe-production-improvement">PE Production Improvement</Link></div></section>}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <h2 style={{ margin:0, fontSize:20, fontWeight:800 }}>Resource Notes</h2>
        <button onClick={() => setShowNewNote(true)}
          style={{ background:'#fb923c', color:'#000', border:'none', borderRadius:6,
            padding:'8px 18px', fontWeight:700, fontSize:13, cursor:'pointer' }}>+ New Note</button>
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap', alignItems:'center' }}>
        <select value={entityType} onChange={e => setEntityType(e.target.value)} style={fs}>
          <option value="">All domains</option>
          {Object.entries(ENTITY_LABELS).map(([k,v])=><option key={k} value={k}>{v}</option>)}
        </select>
        {canManage && (
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={fs}>
            <option value="">All statuses</option>
            {COLUMNS.map(c=><option key={c}>{c}</option>)}
          </select>
        )}
        {canManage && (
          <select value={supplierFilter} onChange={e => setSupplierFilter(e.target.value)} style={fs}>
            <option value="">All suppliers</option>
            {SUPPLIERS.map(s=><option key={s} value={s}>{SUPPLIER_LABELS[s]}</option>)}
          </select>
        )}
        <input
          value={docNumFilter}
          onChange={e => setDocNumFilter(e.target.value)}
          placeholder="Doc number…"
          style={{ ...fs, minWidth:140 }}
        />
        <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer', userSelect:'none' }}>
          <input type="checkbox" checked={blockingOnly} onChange={e => setBlockingOnly(e.target.checked)}
            style={{ accentColor:'#EC2124' }} />
          Blocking only
        </label>
      </div>

      {loading ? (
        <div style={{ padding:40, textAlign:'center', color:'var(--ni-text-muted)' }}>Loading…</div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, alignItems:'start' }}>
          {COLUMNS.map(col => (
            <KanbanColumn
              key={col}
              title={col}
              notes={columns[col] || []}
              canManage={canManage}
              currentUserId={user?.id}
              onMove={moveNote}
              onDelete={deleteNote}
              onToggleBlocking={toggleBlocking}
            />
          ))}
        </div>
      )}

      {showNewNote && (
        <NewNoteModal
          onClose={() => setShowNewNote(false)}
          onCreated={() => { setShowNewNote(false); load(); }}
          getToken={getToken}
          isSupplier={isSupplier}
          currentUser={user}
        />
      )}
    </div>
  );
}

function KanbanColumn({ title, notes, canManage, currentUserId, onMove, onDelete, onToggleBlocking }) {
  const NEXT = { 'Open':'In Progress', 'In Progress':'Resolved', 'Resolved':null };
  const PREV = { 'Open':null, 'In Progress':'Open', 'Resolved':'In Progress' };

  const COL_COLORS = { Open:'#60a5fa', 'In Progress':'#f59e0b', Resolved:'#46BB89' };
  const color = COL_COLORS[title] || '#666';
  const blockingCount = notes.filter(n => n.isBlocking).length;

  return (
    <div style={{ background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)',
      borderRadius:10, overflow:'hidden' }}>
      <div style={{ padding:'10px 14px', borderBottom:'1px solid var(--ni-border-soft)',
        display:'flex', alignItems:'center', gap:8 }}>
        <div style={{ width:8, height:8, borderRadius:'50%', background:color }} />
        <span style={{ fontWeight:700, fontSize:13 }}>{title}</span>
        {blockingCount > 0 && (
          <span style={{ fontSize:10, fontWeight:700, color:'#EC2124', background:'#EC212420',
            borderRadius:4, padding:'1px 6px', letterSpacing:'0.04em' }}>
            {blockingCount} BLOCKING
          </span>
        )}
        <span style={{ marginLeft:'auto', fontSize:12, color:'var(--ni-text-muted)',
          background:'rgba(255,255,255,0.07)', borderRadius:10, padding:'1px 8px' }}>
          {notes.length}
        </span>
      </div>
      <div style={{ padding:'8px', display:'flex', flexDirection:'column', gap:8, minHeight:80 }}>
        {notes.map(note => (
          <NoteCard
            key={note._id}
            note={note}
            canManage={canManage}
            currentUserId={currentUserId}
            nextStatus={NEXT[title]}
            prevStatus={PREV[title]}
            onMove={onMove}
            onDelete={onDelete}
            onToggleBlocking={onToggleBlocking}
          />
        ))}
        {notes.length === 0 && (
          <div style={{ padding:'16px 8px', textAlign:'center', color:'var(--ni-text-muted)', fontSize:12 }}>
            No notes
          </div>
        )}
      </div>
    </div>
  );
}

function NoteCard({ note, canManage, currentUserId, nextStatus, prevStatus, onMove, onDelete, onToggleBlocking }) {
  const pColor = PRIORITY_COLORS[note.priority] || '#666';
  const entityLabel = ENTITY_LABELS[note.entityType] || note.entityType;
  const now = new Date();
  const due = note.dueDate ? new Date(note.dueDate) : null;
  const isOverdue = due && due < now && note.status !== 'Resolved';
  // Staff act on any note; a supplier (or any author) acts on their own notes.
  const isOwn = note.authorId && note.authorId === currentUserId;
  const canAct = canManage || isOwn;

  return (
    <div style={{ background:'rgba(255,255,255,0.04)',
      border: note.isBlocking ? '1px solid #EC212466' : '1px solid var(--ni-border-soft)',
      borderRadius:8, padding:'10px 12px' }}>
      <div style={{ display:'flex', alignItems:'flex-start', gap:6, marginBottom:6 }}>
        {note.isBlocking && (
          <span style={{ fontSize:9, fontWeight:800, color:'#EC2124', background:'#EC212420',
            borderRadius:3, padding:'1px 5px', letterSpacing:'0.06em', flexShrink:0, marginTop:2 }}>
            BLOCKING
          </span>
        )}
        <span style={{ fontSize:10, fontWeight:700, color:pColor, textTransform:'uppercase',
          letterSpacing:'0.05em', flexShrink:0, marginTop:1 }}>
          {note.priority}
        </span>
        <span style={{ fontWeight:600, fontSize:13, flex:1, lineHeight:1.4 }}>
          {note.title || note.content.slice(0,60)}
        </span>
      </div>
      {note.title && (
        <p style={{ margin:'0 0 8px', fontSize:12, color:'var(--ni-text-muted)', lineHeight:1.5 }}>
          {note.content}
        </p>
      )}
      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:8 }}>
        <span style={{ fontSize:10, color:'var(--ni-text-muted)', background:'rgba(255,255,255,0.06)',
          borderRadius:4, padding:'2px 6px' }}>
          {entityLabel}
        </span>
        {note.supplierKey && (
          <span style={{ fontSize:10, fontWeight:700, color:'#2dd4bf', background:'#2dd4bf18',
            borderRadius:4, padding:'2px 6px', textTransform:'uppercase', letterSpacing:'0.04em' }}>
            {SUPPLIER_LABELS[note.supplierKey] || note.supplierKey}
          </span>
        )}
        {note.entityDocumentNumber && (
          <span style={{ fontSize:10, color:'var(--ni-text-muted)', fontFamily:'monospace',
            background:'rgba(255,255,255,0.06)', borderRadius:4, padding:'2px 6px' }}>
            {note.entityDocumentNumber}
          </span>
        )}
        {note.stationName && (
          <span style={{ fontSize:10, color:'var(--ni-text-muted)',
            background:'rgba(255,255,255,0.06)', borderRadius:4, padding:'2px 6px' }}>
            📍 {note.stationName}
          </span>
        )}
        {note.assignedToName && (
          <span style={{ fontSize:10, color:'#60a5fa', background:'#60a5fa18',
            borderRadius:4, padding:'2px 6px' }}>
            → {note.assignedToName}
          </span>
        )}
        {due && (
          <span style={{ fontSize:10, color: isOverdue ? '#EC2124' : 'var(--ni-text-muted)',
            background: isOverdue ? '#EC212418' : 'rgba(255,255,255,0.06)',
            borderRadius:4, padding:'2px 6px', fontWeight: isOverdue ? 700 : 400 }}>
            {isOverdue ? '⚠ ' : ''}Due {due.toLocaleDateString()}
          </span>
        )}
      </div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <span style={{ fontSize:11, color:'var(--ni-text-muted)' }}>
          {note.authorName} · {note.createdAt ? new Date(note.createdAt).toLocaleDateString() : ''}
        </span>
        {canAct && (
          <div style={{ display:'flex', gap:4 }}>
            {onToggleBlocking && (
              <button onClick={() => onToggleBlocking(note._id, !note.isBlocking)}
                title={note.isBlocking ? 'Unmark blocking' : 'Mark as blocking'}
                style={{ background: note.isBlocking ? '#EC212420' : 'none',
                  border:'1px solid var(--ni-border-soft)', color: note.isBlocking ? '#EC2124' : 'var(--ni-text-muted)',
                  borderRadius:4, padding:'2px 8px', cursor:'pointer', fontSize:11 }}>
                ⛔
              </button>
            )}
            {prevStatus && (
              <button onClick={() => onMove(note._id, prevStatus)}
                style={{ background:'none', border:'1px solid var(--ni-border-soft)', color:'var(--ni-text-muted)',
                  borderRadius:4, padding:'2px 8px', cursor:'pointer', fontSize:11 }}>←</button>
            )}
            {nextStatus && (
              <button onClick={() => onMove(note._id, nextStatus)}
                style={{ background:'none', border:'1px solid var(--ni-border-soft)', color:'var(--ni-text-muted)',
                  borderRadius:4, padding:'2px 8px', cursor:'pointer', fontSize:11 }}>→</button>
            )}
            <button onClick={() => onDelete(note._id)}
              style={{ background:'rgba(236,33,36,0.12)', border:'none', color:'#EC2124',
                borderRadius:4, padding:'2px 8px', cursor:'pointer', fontSize:11 }}>✕</button>
          </div>
        )}
      </div>
    </div>
  );
}

function NewNoteModal({ onClose, onCreated, getToken, isSupplier, currentUser }) {
  const [title, setTitle]           = useState('');
  const [content, setContent]       = useState('');
  const [entityType, setEntityType] = useState('process_flow');
  const [entityDocNum, setEntityDocNum] = useState('');
  const [priority, setPriority]     = useState('Normal');
  const [dueDate, setDueDate]       = useState('');
  const [isBlocking, setIsBlocking] = useState(false);
  const [assignedToName, setAssignedToName] = useState('');
  const [saving, setSaving]         = useState(false);

  const myName = `${currentUser?.firstName||''} ${currentUser?.lastName||''}`.trim() || '';

  const submit = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      const token = await getToken();
      await window.fetch(`${API_BASE}/quality/notes`, {
        method:'POST',
        headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
        body: JSON.stringify({
          title, content, entityType,
          entityDocumentNumber: entityDocNum,
          priority: isSupplier ? 'Normal' : priority,
          status: 'Open',
          dueDate: dueDate || null,
          isBlocking: isSupplier ? false : isBlocking,
          assignedToName: assignedToName || null,
        }),
      });
      onCreated();
    } catch {} finally { setSaving(false); }
  };

  const inputStyle = { background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)',
    color:'var(--ni-text)', borderRadius:6, padding:'7px 10px', fontSize:13, width:'100%', boxSizing:'border-box' };
  const labelStyle = { fontSize:11, fontWeight:700, color:'var(--ni-text-muted)', textTransform:'uppercase',
    letterSpacing:'0.05em', display:'block', marginBottom:5 };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'var(--ni-char)', border:'1px solid var(--ni-border-soft)', borderRadius:12,
        padding:24, width:460, maxWidth:'90%', maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
          <span style={{ fontWeight:700, fontSize:16 }}>New Note</span>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--ni-text-muted)', cursor:'pointer', fontSize:18 }}>×</button>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <div>
            <label style={labelStyle}>Domain</label>
            <select value={entityType} onChange={e=>setEntityType(e.target.value)} style={inputStyle}>
              {Object.entries(ENTITY_LABELS).map(([k,v])=><option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Document Number (optional)</label>
            <input value={entityDocNum} onChange={e=>setEntityDocNum(e.target.value)}
              style={inputStyle} placeholder="e.g. PF-NIN-001" />
          </div>
          <div>
            <label style={labelStyle}>Title (optional)</label>
            <input value={title} onChange={e=>setTitle(e.target.value)} style={inputStyle} placeholder="Short summary" />
          </div>
          <div>
            <label style={labelStyle}>Note *</label>
            <textarea value={content} onChange={e=>setContent(e.target.value)} rows={4}
              style={{ ...inputStyle, resize:'vertical' }} placeholder="Describe the observation or action…" />
          </div>
          {!isSupplier && (
            <div>
              <label style={labelStyle}>Priority</label>
              <select value={priority} onChange={e=>setPriority(e.target.value)} style={inputStyle}>
                {['Low','Normal','High','Critical'].map(p=><option key={p}>{p}</option>)}
              </select>
            </div>
          )}
          {!isSupplier && (
            <div style={{ display:'flex', gap:12 }}>
              <div style={{ flex:1 }}>
                <label style={labelStyle}>Due Date</label>
                <input type="date" value={dueDate} onChange={e=>setDueDate(e.target.value)}
                  style={inputStyle} />
              </div>
              <div style={{ display:'flex', flexDirection:'column', justifyContent:'flex-end', paddingBottom:2 }}>
                <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer', userSelect:'none' }}>
                  <input type="checkbox" checked={isBlocking} onChange={e=>setIsBlocking(e.target.checked)}
                    style={{ accentColor:'#EC2124' }} />
                  <span style={{ color: isBlocking ? '#EC2124' : 'var(--ni-text)' }}>Blocking</span>
                </label>
              </div>
            </div>
          )}
          {!isSupplier && (
            <div>
              <label style={labelStyle}>Assign To</label>
              <div style={{ display:'flex', gap:8 }}>
                <input value={assignedToName} onChange={e=>setAssignedToName(e.target.value)}
                  style={{ ...inputStyle, flex:1 }} placeholder="Name…" />
                {myName && (
                  <button type="button" onClick={() => setAssignedToName(myName)}
                    style={{ background:'none', border:'1px solid #60a5fa44', color:'#60a5fa',
                      borderRadius:6, padding:'6px 12px', fontSize:12, cursor:'pointer', whiteSpace:'nowrap' }}>
                    Assign to me
                  </button>
                )}
              </div>
            </div>
          )}
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={submit} disabled={saving||!content.trim()}
              style={{ background:'#fb923c', color:'#000', border:'none', borderRadius:6,
                padding:'8px 20px', fontWeight:700, fontSize:13, cursor:saving?'not-allowed':'pointer' }}>
              {saving?'Saving…':'Add Note'}
            </button>
            <button onClick={onClose}
              style={{ background:'transparent', border:'1px solid var(--ni-border-soft)', color:'var(--ni-text-muted)',
                borderRadius:6, padding:'8px 16px', fontSize:13, cursor:'pointer' }}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
